/* MODLAND PDP R3. Presentation adapter only: move, never clone app/FAQ nodes.
   No SDK loading, global app settings, recommendation data or cart writes. */
(() => {
  'use strict';
  if (window.ModlandPdpExtras) return;
  class PdpExtras {
    constructor(root) {
      this.root = root;
      this.frame = 0;
      this.destroyed = false;
      this.moves = new WeakMap();
      this.observed = new Set();
      this.queue = () => {
        if (this.destroyed || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.sync();
        });
      };
      this.onBlockSelect = (event) => {
        if (!this.faq) return;
        const details = event.target.closest('details');
        if (details && this.faq.contains(details)) details.open = true;
      };
      this.mutation = new MutationObserver(this.queue);
      this.mutation.observe(root, { childList: true, subtree: true });
      this.resize = window.ResizeObserver ? new ResizeObserver(this.queue) : null;
      window.addEventListener('resize', this.queue, { passive: true });
      window.addEventListener('pageshow', this.queue);
      root.addEventListener('toggle', this.queue, true);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.sync();
    }
    variable(element, name, value) {
      if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
    }
    observe(elements) {
      if (!this.resize) return;
      const wanted = new Set(elements.filter(Boolean));
      for (const old of this.observed) if (!wanted.has(old)) this.resize.unobserve(old);
      for (const element of wanted) if (!this.observed.has(element)) this.resize.observe(element);
      this.observed = wanted;
    }
    sync() {
      if (this.destroyed || !this.root.isConnected) return;
      const gallery = this.root.querySelector('.t4s-section-main-product .t4s-product__media-wrapper');
      const origins = [...this.root.querySelectorAll('[data-ml-faq-origin]')];
      // Remove only our stale relocated FAQ after its editor section is deleted/reloaded.
      if (gallery) {
        for (const previous of gallery.querySelectorAll(':scope > [data-ml-gallery-faq]')) {
          const origin = origins.find(el => el.dataset.mlFaqOrigin === previous.dataset.mlFaqOwner);
          if (!origin || origin.querySelector('[data-ml-gallery-faq]')) previous.remove();
        }
        const origin = origins[0];
        const incoming = origin && origin.querySelector('[data-ml-gallery-faq]');
        if (incoming) gallery.appendChild(incoming);
        if (origin) origin.dataset.mlFaqMounted = 'true';
        this.faq = gallery.querySelector(':scope > [data-ml-gallery-faq]');
        if (this.faq) {
          const summaries = [...this.faq.querySelectorAll('summary')];
          this.observe([gallery, this.faq, ...summaries]);
          const margin = parseFloat(getComputedStyle(this.faq).marginTop) || 0;
          const closedHeight = margin + summaries.reduce((sum, summary) => {
            const style = getComputedStyle(summary.parentElement);
            return sum + summary.getBoundingClientRect().height + (parseFloat(style.borderBottomWidth) || 0);
          }, 0);
          this.variable(gallery, '--ml-pdp-faq-reserve', `${Math.ceil(closedHeight)}px`);
          const bottom = parseFloat(getComputedStyle(this.root).getPropertyValue('--ml-pdp-bottom-clearance')) || 0;
          const bottomTop = window.innerHeight - bottom - gallery.getBoundingClientRect().height;
          this.variable(gallery, '--ml-pdp-stack-bottom-top', `${Math.floor(bottomTop)}px`);
          this.root.dataset.mlFaqPlacement = 'gallery';
        } else {
          this.observe([]);
          gallery.style.removeProperty('--ml-pdp-faq-reserve');
          gallery.style.removeProperty('--ml-pdp-stack-bottom-top');
          delete this.root.dataset.mlFaqPlacement;
        }
      }
      this.placeFbt();
    }
    placeFbt() {
      const slot = this.root.querySelector('[data-ml-fbt-slot]');
      if (!slot) return;
      const host = slot.querySelector('#cbb-frequently-bought-box');
      if (!host) return;
      // Never collect widgets belonging to a quick view, other page or outside this template.
      const candidates = [...this.root.querySelectorAll('.cbb-frequently-bought-container')]
        .filter(el => !el.closest('dialog, [role="dialog"], .t4s-modal, .t4s-drawer'));
      if (candidates.length !== 1) {
        slot.dataset.mlFbtStatus = candidates.length ? 'ambiguous' : 'waiting-for-app';
        return;
      }
      const widget = candidates[0];
      if (!slot.contains(widget)) {
        // Fail safely if a provider keeps forcing a conflicting location: no observer tug-of-war.
        const count = this.moves.get(widget) || 0;
        if (count >= 3 || widget.contains(slot)) {
          slot.dataset.mlFbtStatus = 'placement-conflict';
          return;
        }
        host.appendChild(widget);
        this.moves.set(widget, count + 1);
      }
      slot.dataset.mlFbtStatus = 'mounted';
    }
    destroy() {
      this.destroyed = true;
      cancelAnimationFrame(this.frame);
      this.mutation.disconnect();
      if (this.resize) this.resize.disconnect();
      window.removeEventListener('resize', this.queue);
      window.removeEventListener('pageshow', this.queue);
      this.root.removeEventListener('toggle', this.queue, true);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
  }
  let active = null;
  const boot = () => {
    const root = document.getElementById('modland-pdp-v2');
    if (active && active.root !== root) { active.destroy(); active = null; }
    if (!root) return;
    if (active) active.queue();
    else active = new PdpExtras(root);
  };
  window.ModlandPdpExtras = { boot };
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(boot));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
